/*
  One Tooth World — shared UI foundation (PRD §9).
  OTW namespace + event bus, <otw-drawer>, <otw-accordion>, <otw-tooltip>, toasts,
  section fetching and money formatting. No dependencies. Loaded globally as a module.
  Other otw- modules talk to this file only through window.OTW (no ES imports: asset URLs
  carry ?v= cache-busters, so an import would evaluate a second copy of the module).
*/

const OTW = (window.OTW = window.OTW || {});
OTW.strings = OTW.strings || {};

const str = (key, fallback) => OTW.strings[key] || fallback;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Event bus ---------- */
const bus = new EventTarget();

OTW.on = (name, handler) => {
  const listener = (event) => handler(event.detail, event);
  bus.addEventListener(name, listener);
  return () => bus.removeEventListener(name, listener);
};

OTW.off = (name, listener) => bus.removeEventListener(name, listener);

OTW.emit = (name, detail = {}) => {
  bus.dispatchEvent(new CustomEvent(name, { detail }));
};

OTW.reducedMotion = () => reducedMotionQuery.matches;

/* ---------- Helpers ---------- */
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

OTW.focusable = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => !el.closest('[inert]') && (el.offsetWidth || el.offsetHeight || el.getClientRects().length)
  );

OTW.debounce = (fn, wait = 200) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

/* Scroll lock with a counter so stacked drawers don't unlock early */
let scrollLocks = 0;

OTW.lockScroll = () => {
  if (scrollLocks++ === 0) {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--otw-scrollbar-width', `${scrollbar}px`);
    document.documentElement.classList.add('otw-scroll-locked');
  }
};

OTW.unlockScroll = () => {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) {
    document.documentElement.classList.remove('otw-scroll-locked');
    document.documentElement.style.removeProperty('--otw-scrollbar-width');
  }
};

/* Make everything outside `keep` inert: siblings of each ancestor up to <body> */
function inertOutside(keep) {
  const changed = [];
  let node = keep;
  while (node && node.parentElement && node !== document.body) {
    for (const sibling of node.parentElement.children) {
      if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(sibling.tagName)) {
        continue;
      }
      if (sibling.classList.contains('otw-toast-region')) continue;
      sibling.inert = true;
      changed.push(sibling);
    }
    node = node.parentElement;
  }
  return () => changed.forEach((el) => (el.inert = false));
}

/* ---------- <otw-drawer> ---------- */
/*
  Markup:
  <otw-drawer id="Drawer-x" side="left|right|bottom" [data-desktop="modal"]>
    <div class="otw-drawer__backdrop" data-drawer-close></div>
    <div class="otw-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="…" tabindex="-1">
      <div class="otw-drawer__handle" data-drawer-handle></div>   (bottom sheets)
      … <button data-drawer-close>…</button> …
    </div>
  </otw-drawer>
  Open with <button data-drawer-open="Drawer-x" aria-controls="Drawer-x">, or element.open().
*/
const drawerStack = [];

class OtwDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.otw-drawer__panel');
    if (!this.panel) return;
    if (!this.panel.hasAttribute('role')) this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    if (!this.panel.hasAttribute('tabindex')) this.panel.setAttribute('tabindex', '-1');
    this.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');

    this.onClick = (event) => {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
      }
    };
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    if (this.getAttribute('side') === 'bottom') this.setupSwipe();
  }

  disconnectedCallback() {
    if (this.isOpen) this.close({ restoreFocus: false });
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open(opener) {
    if (this.isOpen || !this.panel) return;
    this.opener = opener || document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.releaseInert = inertOutside(this);
    OTW.lockScroll();
    drawerStack.push(this);
    if (this.opener && this.opener.setAttribute && this.opener.hasAttribute('aria-controls')) {
      this.opener.setAttribute('aria-expanded', 'true');
    }

    // Focus after the panel becomes visible
    requestAnimationFrame(() => {
      const target =
        this.panel.querySelector('[autofocus]') ||
        this.panel.querySelector('[data-drawer-initial-focus]') ||
        this.panel;
      target.focus({ preventScroll: true });
    });

    this.dispatchEvent(new CustomEvent('otw:drawer:open', { bubbles: true }));
    OTW.emit('drawer:open', { id: this.id, drawer: this });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.panel.style.transform = '';
    if (this.releaseInert) this.releaseInert();
    this.releaseInert = null;
    OTW.unlockScroll();
    const index = drawerStack.indexOf(this);
    if (index > -1) drawerStack.splice(index, 1);
    if (this.opener && this.opener.setAttribute && this.opener.hasAttribute('aria-controls')) {
      this.opener.setAttribute('aria-expanded', 'false');
    }
    if (restoreFocus && this.opener && document.contains(this.opener)) {
      this.opener.focus({ preventScroll: true });
    }
    this.opener = null;
    this.dispatchEvent(new CustomEvent('otw:drawer:close', { bubbles: true }));
    OTW.emit('drawer:close', { id: this.id, drawer: this });
  }

  toggle(opener) {
    this.isOpen ? this.close() : this.open(opener);
  }

  onKeydown(event) {
    if (!this.isOpen || drawerStack[drawerStack.length - 1] !== this) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = OTW.focusable(this.panel);
    if (!items.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Bottom sheet: drag the handle down to close */
  setupSwipe() {
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    let dragging = false;

    const onDown = (event) => {
      const handle = event.target.closest('[data-drawer-handle]');
      if (!handle || !this.isOpen) return;
      dragging = true;
      startY = lastY = event.clientY;
      lastT = event.timeStamp;
      velocity = 0;
      this.classList.add('is-dragging');
      handle.setPointerCapture?.(event.pointerId);
    };
    const onMove = (event) => {
      if (!dragging) return;
      const dy = Math.max(0, event.clientY - startY);
      const dt = Math.max(1, event.timeStamp - lastT);
      velocity = (event.clientY - lastY) / dt;
      lastY = event.clientY;
      lastT = event.timeStamp;
      this.panel.style.transform = `translateY(${dy}px)`;
    };
    const onUp = (event) => {
      if (!dragging) return;
      dragging = false;
      this.classList.remove('is-dragging');
      const dy = Math.max(0, event.clientY - startY);
      const threshold = Math.min(120, this.panel.offsetHeight * 0.25);
      if (dy > threshold || velocity > 0.5) {
        this.close();
      } else {
        this.panel.style.transform = '';
      }
    };

    this.panel.addEventListener('pointerdown', onDown);
    this.panel.addEventListener('pointermove', onMove);
    this.panel.addEventListener('pointerup', onUp);
    this.panel.addEventListener('pointercancel', onUp);
  }
}

if (!customElements.get('otw-drawer')) customElements.define('otw-drawer', OtwDrawer);

OTW.openDrawer = (id, opener) => document.getElementById(id)?.open?.(opener);
OTW.closeDrawer = (id) => document.getElementById(id)?.close?.();

/* ---------- <otw-accordion> ---------- */
/*
  <otw-accordion [data-single]>
    <div class="otw-accordion__item">
      <button class="otw-accordion__trigger" aria-expanded="false" aria-controls="P1">Title</button>
      <div class="otw-accordion__panel" id="P1" role="region"><div><div class="otw-accordion__content">…</div></div></div>
    </div>
  </otw-accordion>
  Height animates with grid-template-rows 0fr → 1fr (CSS in otw-base.css).
*/
class OtwAccordion extends HTMLElement {
  connectedCallback() {
    this.onClick = (event) => {
      const trigger = event.target.closest('.otw-accordion__trigger');
      if (!trigger || trigger.closest('otw-accordion') !== this) return;
      event.preventDefault();
      this.toggle(trigger);
    };
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get triggers() {
    return Array.from(this.querySelectorAll('.otw-accordion__trigger')).filter(
      (t) => t.closest('otw-accordion') === this
    );
  }

  toggle(trigger, force) {
    const expand = force ?? trigger.getAttribute('aria-expanded') !== 'true';
    if (expand && this.hasAttribute('data-single')) {
      this.triggers.forEach((t) => t !== trigger && t.setAttribute('aria-expanded', 'false'));
    }
    trigger.setAttribute('aria-expanded', String(expand));
    this.dispatchEvent(new CustomEvent('otw:accordion:toggle', { bubbles: true, detail: { trigger, expanded: expand } }));
  }
}

if (!customElements.get('otw-accordion')) customElements.define('otw-accordion', OtwAccordion);

/* ---------- <otw-tooltip> ---------- */
/*
  <otw-tooltip>
    <button type="button" class="otw-tooltip__trigger" aria-expanded="false" aria-controls="T1" aria-label="More information">?</button>
    <div class="otw-tooltip__bubble" id="T1" role="status" hidden>Text</div>
  </otw-tooltip>
  Click / tap toggles; outside click and Esc close.
*/
const openTooltips = new Set();

class OtwTooltip extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.otw-tooltip__trigger');
    this.bubble = this.querySelector('.otw-tooltip__bubble');
    if (!this.trigger || !this.bubble) return;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.onTrigger = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.isOpen ? this.close() : this.open();
    };
    this.trigger.addEventListener('click', this.onTrigger);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTrigger);
    openTooltips.delete(this);
  }

  get isOpen() {
    return !this.bubble.hidden;
  }

  open() {
    openTooltips.forEach((tip) => tip !== this && tip.close());
    this.bubble.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    openTooltips.add(this);
    this.position();
  }

  close() {
    if (!this.bubble || this.bubble.hidden) return;
    this.bubble.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.bubble.style.removeProperty('--otw-tip-shift');
    this.bubble.removeAttribute('data-placement');
    openTooltips.delete(this);
  }

  /* Keep the bubble inside the viewport */
  position() {
    const margin = 12;
    this.bubble.style.removeProperty('--otw-tip-shift');
    this.bubble.removeAttribute('data-placement');
    let rect = this.bubble.getBoundingClientRect();
    if (rect.top < margin) {
      this.bubble.setAttribute('data-placement', 'bottom');
      rect = this.bubble.getBoundingClientRect();
    }
    let shift = 0;
    if (rect.left < margin) shift = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) shift = window.innerWidth - margin - rect.right;
    if (shift) this.bubble.style.setProperty('--otw-tip-shift', `${shift}px`);
  }
}

if (!customElements.get('otw-tooltip')) customElements.define('otw-tooltip', OtwTooltip);

document.addEventListener('click', (event) => {
  openTooltips.forEach((tip) => {
    if (!tip.contains(event.target)) tip.close();
  });
});

/* ---------- Toasts (PRD §6.10) ---------- */
let toastRegion;

function getToastRegion() {
  if (toastRegion && document.body.contains(toastRegion)) return toastRegion;
  // The layout renders one region; create it only if a template has none
  toastRegion = document.querySelector('.otw-toast-region');
  if (toastRegion) return toastRegion;
  toastRegion = document.createElement('div');
  toastRegion.className = 'otw-toast-region';
  toastRegion.setAttribute('role', 'status');
  toastRegion.setAttribute('aria-live', 'polite');
  toastRegion.setAttribute('aria-atomic', 'false');
  document.body.appendChild(toastRegion);
  return toastRegion;
}

const CHECK_ICON =
  '<svg class="otw-icon otw-toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>';

/**
 * OTW.toast('Added to wishlist', { action: 'View', href: '/pages/wishlist' })
 * Options: action (label), href (link) or onAction (callback), duration (ms, default 3000), icon (bool, default true).
 * Returns { dismiss }.
 */
OTW.toast = (message, { action, href, onAction, duration = 3000, icon = true } = {}) => {
  const region = getToastRegion();
  const toast = document.createElement('div');
  toast.className = 'otw-toast';

  if (icon) toast.insertAdjacentHTML('beforeend', CHECK_ICON);
  const text = document.createElement('span');
  text.className = 'otw-toast__message';
  text.textContent = message;
  toast.appendChild(text);

  if (action && (href || onAction)) {
    const button = document.createElement(href ? 'a' : 'button');
    button.className = 'otw-toast__action';
    button.textContent = action;
    if (href) button.href = href;
    else button.type = 'button';
    button.addEventListener('click', (event) => {
      if (onAction) onAction(event);
      dismiss();
    });
    toast.appendChild(button);
  }

  // Keep at most 3 on screen
  while (region.children.length >= 3) region.firstElementChild.remove();
  region.appendChild(toast);
  requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-visible')));

  let timer;
  let removed = false;
  const start = () => {
    clearTimeout(timer);
    timer = setTimeout(dismiss, duration);
  };
  function dismiss() {
    if (removed) return;
    removed = true;
    clearTimeout(timer);
    toast.classList.remove('is-visible');
    const remove = () => toast.remove();
    if (OTW.reducedMotion()) remove();
    else {
      toast.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 400);
    }
  }
  toast.addEventListener('mouseenter', () => clearTimeout(timer));
  toast.addEventListener('mouseleave', start);
  toast.addEventListener('focusin', () => clearTimeout(timer));
  toast.addEventListener('focusout', start);
  start();
  OTW.emit('toast', { message });
  return { dismiss };
};

/* ---------- Button state helper (spinner → "Added ✓") ---------- */
OTW.setButtonState = (button, state, label) => {
  if (!button) return;
  if (!button.dataset.otwLabel) button.dataset.otwLabel = button.innerHTML;
  button.classList.remove('is-loading', 'is-success');
  button.removeAttribute('aria-busy');
  if (state === 'loading') {
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
  } else if (state === 'success') {
    button.classList.add('is-success');
    button.textContent = label || str('added', 'Added ✓');
  } else {
    button.innerHTML = button.dataset.otwLabel;
    delete button.dataset.otwLabel;
  }
};

/* ---------- Section rendering ---------- */
/**
 * Fetches `url` rendered with only `sectionId` and returns the section element
 * (#shopify-section-<id>, or the first element of the response).
 */
OTW.fetchSection = async (url, sectionId, { signal } = {}) => {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  const response = await fetch(target.toString(), {
    signal,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok) throw new Error(`OTW.fetchSection: ${response.status} for ${target.pathname}`);
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.getElementById(`shopify-section-${sectionId}`) || doc.body.firstElementChild;
};

/* ---------- Money ---------- */
const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
  if (cents == null || Number.isNaN(Number(cents))) return '0';
  const number = (Number(cents) / 100).toFixed(precision);
  const [whole, fraction] = number.split('.');
  const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
  return fraction ? `${grouped}${decimal}${fraction}` : grouped;
}

/**
 * OTW.money(49900) → "₹499" using the shop's money_format (injected in the layout).
 * Trailing zeros are stripped to match Liquid's money_without_trailing_zeros.
 */
OTW.money = (cents, format = OTW.moneyFormat) => {
  const value = typeof cents === 'string' ? Number(cents.replace(/[^\d.-]/g, '')) : cents;
  if (!format) {
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value / 100);
    } catch {
      return String(value / 100);
    }
  }
  const match = format.match(MONEY_PLACEHOLDER);
  if (!match) return format;
  let amount;
  switch (match[1]) {
    case 'amount_no_decimals':
      amount = formatWithDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      amount = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      amount = formatWithDelimiters(value, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      amount = formatWithDelimiters(value, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      amount = formatWithDelimiters(value, 0, ' ', '');
      break;
    case 'amount_with_space_separator':
      amount = formatWithDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      amount = formatWithDelimiters(value, 2, ' ', '.');
      break;
    default:
      amount = formatWithDelimiters(value, 2);
  }
  amount = amount.replace(/[.,]00$/, '');
  return format.replace(MONEY_PLACEHOLDER, amount);
};

/** Discount % = floor((compare − price) / compare × 100) */
OTW.discountPercent = (price, compare) =>
  compare > price && compare > 0 ? Math.floor(((compare - price) / compare) * 100) : 0;

/* ---------- Count bubbles (cart / wishlist) ---------- */
/**
 * OTW.updateBubbles('cart', 3) updates every [data-otw-cart-count] element:
 * text, hidden at 0, and a bounce when the value changes. Links marked
 * [data-otw-count-label="Cart, [count] items"] get a matching aria-label.
 */
OTW.counts = OTW.counts || {};

OTW.updateBubbles = (kind, count) => {
  const value = Math.max(0, Number(count) || 0);
  const previous = OTW.counts[kind];
  OTW.counts[kind] = value;
  document.querySelectorAll(`[data-otw-${kind}-count]`).forEach((bubble) => {
    const changed = bubble.textContent.trim() !== String(value);
    bubble.textContent = value > 99 ? '99+' : String(value);
    bubble.hidden = value === 0;
    if (changed && previous !== undefined && value > 0 && !OTW.reducedMotion()) {
      bubble.classList.remove('is-bumped');
      void bubble.offsetWidth; // restart the animation
      bubble.classList.add('is-bumped');
    }
    const labelled = bubble.closest('[data-otw-count-label]');
    if (labelled) labelled.setAttribute('aria-label', labelled.dataset.otwCountLabel.replace('[count]', value));
  });
  OTW.emit(`${kind}:count`, { count: value });
};

document.addEventListener('animationend', (event) => {
  if (event.target.classList?.contains('is-bumped')) event.target.classList.remove('is-bumped');
});

/* ---------- Cart open (fallback until otw-cart.js exists) ---------- */
/* OTW.emit('cart:open', { opener }) → OTW.cart.open() when present, else Dawn's
   <cart-drawer>, else the /cart page. */
OTW.on('cart:open', () => {
  if (OTW.cart && typeof OTW.cart.open === 'function') return; // otw-cart.js listens itself
  window.location.href = window.routes?.cart_url || '/cart';
});

/* ---------- Declarative hooks ---------- */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (opener) {
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.toggle(opener);
    }
    return;
  }

  const toastTrigger = event.target.closest('[data-otw-toast]');
  if (toastTrigger) {
    OTW.toast(toastTrigger.dataset.otwToast, {
      action: toastTrigger.dataset.otwToastAction,
      href: toastTrigger.dataset.otwToastHref,
    });
  }
});

OTW.emit('ui:ready');
document.dispatchEvent(new CustomEvent('otw:ui:ready'));
